import {
  AfterViewInit,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from "@angular/core";
import { NgbModal, NgbModalConfig } from "@ng-bootstrap/ng-bootstrap";
import { EMPTY, forkJoin, of, Subject, timer } from "rxjs";
import { catchError, expand, map, reduce, switchMap, takeUntil } from "rxjs/operators";
import moment from "moment";
import "moment/locale/es";

import { StorageService } from "../../routes/services/storage.service";
import { GlobalFunction } from "../../routes/services/globalFuntion";
import { FarmService } from "../../routes/services/farm.service";
import { FramesService } from "../../routes/services/frames.service";
import { FeedersService } from "../../routes/services/feeder.service";
import { TranslatorService } from "../../core/translator/translator.service";

import {
  PANEL_COMMANDS,
  PANEL_POWER_COMMANDS,
  PANEL_QUEUE_PAGE_SIZE,
  PANEL_QUEUE_POLL_MS,
  PANEL_QUEUE_SENT_STATUS,
  XBEE_TRANSMIT_FRAME_TYPES
} from "./constants/panel-alerts.constants";
import {
  FrameQueueItem,
  PanelAlertEntry,
  PanelCommandDefinition,
  PanelCommandItem,
  PanelCommandKey,
  PanelEquipmentCounters,
  PanelFeeder,
  PanelFilters,
  PanelGatewayInfo,
  PanelProgress,
  PanelQueueStatus,
  PanelQueueSummary
} from "./interfaces/panel-alerts.interface";
import {
  applyFilters,
  cloneFeeder,
  commandFromSocket,
  computeCommandItems,
  computeEquipmentCounters,
  computeProgress,
  countCommandTargets,
  lastResponseAt,
  normalizeFeeder,
  sentCommands
} from "./utils/panel-alerts.utils";

/** Cadencia con la que se refresca la etiqueta de "ultima respuesta". */
const LAST_RESPONSE_TICK_MS = 1000;


/**
 * Modal de testeos automaticos para alimentadores.
 *
 * Es el unico dueno del estado del panel: recibe los alimentadores, envia los
 * comandos, escucha las respuestas del socket y publica hacia los componentes
 * hijos el estado ya calculado. Cualquier cambio pasa por `commit`, que
 * reconstruye la lista con referencias nuevas y recalcula de una sola vez los
 * contadores, el progreso y los filtros.
 */
/** Mensajes del backend que indican gateway desconectado (es / en). */
const GATEWAY_DOWN_PATTERN = /gateway.*(desconectad|disconnected)/i;

@Component({
  selector: "app-panel-alerts",
  templateUrl: "./panel-alerts.html",
  styleUrls: ["./panel-alerts.scss"],
  providers: [NgbModalConfig, NgbModal]
})
export class PanelAlertsComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild("panelAlerts") public panelAlerts;

  /** Alimentadores sobre los que se va a trabajar. */
  @Input() list: any[] = [];

  /** Se emite al cerrar el panel para que el contenedor lo desmonte. */
  @Output() closed = new EventEmitter<boolean>();

  /** Estado maestro del panel. */
  feeders: PanelFeeder[] = [];

  /* --- Estado derivado, recalculado en cada commit --- */

  /** Alimentadores que pasan los filtros activos. */
  visibleFeeders: PanelFeeder[] = [];
  /** Comandos visibles con su estado y su conteo de respuestas. */
  commandItems: PanelCommandItem[] = [];
  /** Reparto de equipos entre respondidos, en espera y con error. */
  counters: PanelEquipmentCounters = { answered: 0, waiting: 0, with_error: 0 };
  /** Progreso de envio de comandos del lote actual. */
  progress: PanelProgress = { sent: 0, total: 0, percentage: 0 };
  /** Comandos que ya se enviaron en este lote. */
  sentCommandKeys: PanelCommandKey[] = [];
  /** Texto relativo de la ultima respuesta recibida. */
  lastResponseLabel = "";
  /** Comandos esperando respuesta y terminados, para el boton de la hoja en compacto. */
  commandsSummary = { waiting: 0, finished: 0 };
  /** Equipos en espera cuyo frame sigue en la cola del backend o ya salio de ella. */
  queueSummary: PanelQueueSummary = { waiting: 0, sent: 0 };

  /** En compacto (< 1400px), si la hoja inferior de comandos esta abierta. */
  commandsSheetOpen = false;
  /** Admin cierra sin confirmar, asi que la hoja puede salir deslizandose al arrastrarla. */
  isAdmin = false;

  /* --- Contexto --- */

  filters: PanelFilters = { name: "", statusFeeders: "" };
  /** Gateways de la granja, para el indicador del pie. */
  gateways: PanelGatewayInfo[] = [];
  /** Gateway que respondio la ultima trama. */
  activeGatewayId: number = null;
  /** Zona horaria de la granja, para mostrar las horas de las tramas. */
  timezone = "";
  /** Comandos que el usuario actual puede ver. */
  visibleCommands: PanelCommandDefinition[] = [];

  /* --- Control del modal --- */

  modalRef: any = null;
  close = false;
  initialValue = false;

  private destroy$ = new Subject<void>();
  private lastResponseAtIso = "";
  /** Equipos a los que se hizo la peticion de cada comando. */
  private commandTargets: { [key: string]: number } = {};
  /** Emite para detener la consulta periodica de la cola de frames. */
  private stopQueue$ = new Subject<void>();
  /** `true` mientras se consulta periodicamente la cola de frames. */
  private queuePolling = false;
  /** Mayor id de registro de la cola visto hasta ahora. */
  private queueLastId = 0;

  constructor(
    public globalFunction: GlobalFunction,
    public storageService: StorageService,
    public farmService: FarmService,
    public framesService: FramesService,
    public feedersService: FeedersService,
    public translatorService: TranslatorService,
    private modalService: NgbModal,
    config: NgbModalConfig
  ) {
    config.backdrop = "static";
    config.keyboard = false;
    config.size = "xl";
    config.centered = true;
    config.animation = false;
    // Compacto (< 1400px): el modal se muestra como hoja inferior (bottom-sheet.scss)
    config.windowClass = "modal-sheet";
  }

  /**
   * Normaliza los alimentadores recibidos y arranca las escuchas del panel.
   * @returns void
   */
  ngOnInit(): void {
    this.isAdmin = this.globalFunction.validateSoloAdmin();
    this.visibleCommands = this.resolveVisibleCommands();
    this.feeders = (this.list || []).map(normalizeFeeder);
    this.recalculate();

    this.watchSocket();
    this.loadTimezone();
    this.loadGateways();
    this.watchLastResponse();
  }

  /**
   * Abre el modal una vez que la plantilla esta disponible.
   * @returns void
   */
  ngAfterViewInit(): void {
    this.openModal();
  }

  /**
   * Cierra todas las suscripciones y temporizadores del panel.
   * @returns void
   */
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Cierra el panel cuando el usuario hace clic fuera de el.
   *
   * Se ignoran los elementos que abren dialogos propios (confirmaciones de
   * SweetAlert y la casilla de seleccion de equipo), porque un clic en ellos no
   * significa que el usuario quiera salir del panel.
   * @param event Evento de clic del documento.
   */
  @HostListener("document:click", ["$event"])
  onClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const panel = document.querySelector(`[id='panel-alerts']`);
    const isSwal = target.className && String(target.className).indexOf("swal2-") >= 0;

    if (
      this.initialValue &&
      target.id !== "check-feeder" &&
      this.modalRef !== null &&
      !isSwal &&
      panel &&
      !panel.contains(target) &&
      !this.close
    ) {
      this.closeModal();
      event.stopPropagation();
    }
  }

  /**
   * Lanza un comando sobre los equipos seleccionados, o sobre todos si no hay
   * ninguno marcado.
   * @param key Comando a enviar.
   */
  runCommand(key: PanelCommandKey): void {
    this.commandsSheetOpen = false;
    const targets = this.resolveTargets(key);
    if (targets.length === 0) {
      return;
    }

    this.commandTargets[key] = targets.length;
    const ids = targets.map((feeder) => feeder.id);
    this.markLoading(ids, key);

    const request =
      key === "refeed"
        ? this.framesService.refeed({ ponds: this.resolvePonds(targets) })
        : this.framesService.Frame(this.endpointFor(key) as any, `?id_in=${ids}`);

    request.pipe(takeUntil(this.destroy$)).subscribe(
      (resp) => this.applyCommandErrors(key, resp),
      (err) => this.applyCommandErrors(key, err)
    );
    /* Si el lote supera el limite de envio, el backend encola los excedentes. */
    this.startQueuePolling();
  }

  /**
   * Reenvia un comando concreto a un solo equipo desde su tarjeta.
   * @param event Equipo y alerta sobre los que se reintenta.
   */
  retryCommand(event: { feeder: PanelFeeder; alert: PanelAlertEntry }): void {
    const { feeder, alert } = event;
    this.markLoading([feeder.id], alert.alert);
    this.framesService
      .Frame(this.endpointFor(alert.alert) as any, `?id_in=${feeder.id}`)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (resp) => this.applyCommandErrors(alert.alert, resp),
        (err) => this.applyCommandErrors(alert.alert, err)
      );
    this.startQueuePolling();
  }

  /** Abre la hoja inferior de comandos (solo se ve en compacto). */
  openCommandsSheet(): void {
    this.commandsSheetOpen = true;
  }

  /** Cierra la hoja inferior de comandos. */
  closeCommandsSheet(): void {
    this.commandsSheetOpen = false;
  }

  /**
   * Alterna la seleccion de un equipo.
   * @param id Identificador del alimentador.
   */
  toggleCheck(id: number): void {
    this.commit((feeders) =>
      feeders.map((feeder) => (feeder.id === id ? { ...feeder, checked: !feeder.checked } : feeder))
    );
  }

  /**
   * Aplica los filtros de la barra de busqueda.
   * @param filters Filtros seleccionados por el usuario.
   */
  changeFilters(filters: PanelFilters): void {
    this.filters = { ...filters };
    this.commit((feeders) => feeders);
  }

  /**
   * Registra la respuesta del vaciado, que tiene su propio componente y su
   * propio endpoint.
   * @param event Respuesta del backend, equipos afectados y si se estaba deteniendo.
   */
  submitEmpty(event: { data: any; feeders: any[]; empty: boolean }): void {
    this.commandsSheetOpen = false;
    /* Mientras se vacia no se cierra el panel al hacer clic fuera. */
    this.close = !!event.empty;

    const affected = (event.feeders || []).map((feeder) => feeder.id);
    const errors = event.data && event.data.errors ? event.data.errors : null;
    const time = moment().format("HH:mm:ss");

    this.commit((feeders) =>
      feeders.map((feeder) => {
        if (affected.indexOf(feeder.id) < 0) {
          return feeder;
        }
        if (errors && Object.prototype.hasOwnProperty.call(errors, feeder.mac)) {
          return {
            ...feeder,
            condition: false,
            text_condition: this.emptyErrorText(errors[feeder.mac], event.feeders)
          };
        }
        return {
          ...feeder,
          condition: true,
          text_condition: "",
          loadings: { ...feeder.loadings, empty: "loading" as const },
          alerts: this.upsertAlert(feeder.alerts, "empty", time)
        };
      })
    );
  }

  /** Abre el modal y arma el cierre por clic exterior una vez visible. */
  openModal = async () => {
    const ref = await this.modalService.open(this.panelAlerts);
    ref.shown.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.modalRef = ref;
      timer(100)
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.initialValue = true;
        });
    });
  };

  /** Cierra el panel, pidiendo confirmacion cuando no es un administrador. */
  closeModal = async () => {
    this.close = true;

    if (this.globalFunction.validateSoloAdmin()) {
      this.dismiss();
      return;
    }

    const confirmed = await this.globalFunction.optionSwalAlert(
      this.translatorService.translate("panel-alerts.close-panel-confirm")
    );
    if (confirmed && this.close) {
      this.dismiss();
      return;
    }
    timer(300)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.close = false;
      });
  };

  /* ------------------------------------------------------------------ */
  /* Estado                                                              */
  /* ------------------------------------------------------------------ */

  /**
   * Punto unico de mutacion del panel.
   *
   * Sustituye la lista por la que devuelve `mutate` y recalcula todo el estado
   * derivado. Al cambiar la referencia del array, los componentes hijos se
   * refrescan sin necesidad de banderas que fuercen la deteccion de cambios.
   * @param mutate Funcion que recibe la lista actual y devuelve una lista nueva.
   */
  private commit(mutate: (feeders: PanelFeeder[]) => PanelFeeder[]): void {
    this.feeders = this.clearAnsweredQueue(mutate(this.feeders));
    this.recalculate();
  }

  /** Recalcula el estado derivado a partir de la lista actual. */
  private recalculate(): void {
    this.sentCommandKeys = sentCommands(this.feeders, this.visibleCommands);
    this.commandItems = computeCommandItems(this.feeders, this.visibleCommands);
    this.commandsSummary = {
      waiting: this.commandItems.filter((item) => item.status === "waiting").length,
      finished: this.commandItems.filter((item) => item.status === "finished").length
    };
    this.counters = computeEquipmentCounters(this.feeders, this.sentCommandKeys);
    this.progress = computeProgress(this.feeders, this.sentCommandKeys);
    this.visibleFeeders = applyFilters(this.feeders, this.filters);
    this.queueSummary = this.buildQueueSummary();
    /* En cuanto ningun equipo espera respuesta (por ejemplo, la ultima trama
       llego por socket) se deja de consultar la cola sin esperar al siguiente ciclo. */
    if (this.queuePolling && !this.feeders.some((feeder) => this.isWaitingResponse(feeder))) {
      this.stopQueuePolling();
    }
    this.lastResponseAtIso = lastResponseAt(this.feeders);
    this.lastResponseLabel = this.buildLastResponseLabel();
  }

  /* ------------------------------------------------------------------ */
  /* Socket                                                              */
  /* ------------------------------------------------------------------ */

  /** Escucha las tramas del socket y las aplica sobre el estado del panel. */
  private watchSocket(): void {
    this.storageService
      .watchStorage("socket")
      .pipe(takeUntil(this.destroy$))
      .subscribe((msg: any) => {
        if (!msg) {
          return;
        }
        if (msg.type === "gateway.connected" || msg.type === "gateway.disconnected") {
          this.applyGatewayStatus(msg);
          return;
        }
        this.applyFrame(msg);
      });
  }

  /**
   * Actualiza el indicador de gateway cuando el socket avisa de una conexion o
   * una desconexion.
   * @param msg Mensaje de estado de gateway.
   */
  private applyGatewayStatus(msg: any): void {
    const connected = msg.type === "gateway.connected";
    this.gateways = this.gateways.map((gateway) =>
      gateway.id === msg.gateway_id ? { ...gateway, connected } : gateway
    );
  }

  /**
   * Aplica una trama de alimentador sobre la lista.
   *
   * El parseo de la trama sigue delegado en `GlobalFunction.setDataFeeders`,
   * que trabaja sobre una copia para no tocar el estado del componente; aqui
   * solo se decide que comando cierra y con que resultado.
   * @param msg Mensaje recibido por el socket.
   */
  private applyFrame(msg: any): void {
    const working = this.feeders.map(cloneFeeder);
    const parsed = this.globalFunction.setDataFeeders(working, msg);
    if (!parsed.feederChanged || parsed.feederChanged.length === 0) {
      return;
    }

    const command = commandFromSocket(msg.data);
    const receivedAt = new Date().toISOString();
    const changedIds = parsed.feederChanged.map((feeder) => feeder.id);

    if (msg.gateway) {
      this.activeGatewayId = msg.gateway;
    }

    this.commit(() =>
      parsed.feeders.map((feeder) => {
        if (changedIds.indexOf(feeder.id) < 0) {
          return feeder;
        }
        return this.resolveFeederFrame(feeder, command, receivedAt, msg.data);
      })
    );
  }

  /**
   * Cierra el comando que responde la trama para un equipo concreto.
   *
   * Cuando el gateway reporta fallo de comunicacion solo se marca en error el
   * comando que la propia alerta identifica; el resto sigue esperando. Si la
   * alerta no dice de que comando se trata, se marcan todos los pendientes.
   * @param feeder Alimentador ya actualizado por el parseo de la trama.
   * @param command Comando que cierra la trama, si se pudo resolver.
   * @param receivedAt Momento en que se recibio la trama.
   * @param msgData Datos crudos de la trama; se guardan en alert.data para los chips del monitor.
   * @returns Alimentador con su estado de comandos y su historial al dia.
   */
  private resolveFeederFrame(
    feeder: PanelFeeder,
    command: PanelCommandKey | "",
    receivedAt: string,
    msgData: any = null
  ): PanelFeeder {
    const loadings = { ...feeder.loadings };
    const pending = Object.keys(loadings).filter((key) => loadings[key] === "loading");

    if (!feeder.condition) {
      const reported = feeder.alerts.filter((alert) => alert.alert_comm).map((item) => item.alert);
      const failing = reported.length > 0 ? reported : pending;
      failing.forEach((key) => {
        if (loadings[key] === "loading") {
          loadings[key] = "error";
        }
      });
      const failedAlerts = feeder.alerts.map((alert) => {
        return failing.indexOf(alert.alert) >= 0 && !alert.data ? { ...alert, error: true } : alert;
      });
      return { ...feeder, loadings, alerts: failedAlerts, last_response_at: receivedAt };
    }

    /* Tambien se cierra un comando en error: si la respuesta del equipo llega
       despues de que el servidor lo diera por fallido, la fila se corrige. */
    const closable = command && (loadings[command] === "loading" || loadings[command] === "error");
    if (!closable) {
      return { ...feeder, last_response_at: receivedAt };
    }

    loadings[command] = "done";
    const alertData = msgData ? { ...msgData } : {};
    const alerts = feeder.alerts.map((alert) => {
      if (alert.alert !== command) {
        return alert;
      }
      return {
        ...alert,
        error: false,
        server_error: false,
        time: this.elapsedSince(alert.timeInitial),
        received_at: receivedAt,
        data: alertData
      };
    });

    return { ...feeder, loadings, alerts, last_response_at: receivedAt };
  }

  /* ------------------------------------------------------------------ */
  /* Cola de frames                                                      */
  /* ------------------------------------------------------------------ */

  /**
   * Consulta periodicamente la cola de frames de los gateways del lote.
   *
   * Cuando se envian demasiados frames el backend los encola y los despacha
   * de a poco; la cola indica cuales siguen en espera y cuales ya salieron
   * hacia el gateway. Se detiene sola cuando ningun equipo espera respuesta.
   */
  private startQueuePolling(): void {
    if (this.queuePolling) {
      return;
    }
    this.queuePolling = true;

    timer(0, PANEL_QUEUE_POLL_MS)
      .pipe(
        switchMap(() => this.fetchQueue(this.queuePollSinceId())),
        takeUntil(this.stopQueue$),
        takeUntil(this.destroy$)
      )
      .subscribe((items: FrameQueueItem[]) => this.applyQueue(items));
  }

  /**
   * Toma como referencia el ultimo registro que ya hay en la cola, para que los
   * envios de la ultima hora no se confundan con los de este panel.
   */
  private loadQueueBaseline(): void {
    /* Basta la ultima pagina: solo interesa el id mas reciente. */
    this.fetchQueue(Number.MAX_SAFE_INTEGER)
      .pipe(takeUntil(this.destroy$))
      .subscribe((items: FrameQueueItem[]) => {
        this.queueLastId = Math.max(this.queueLastId, this.maxQueueId(items));
      });
  }

  /**
   * Mayor id de un grupo de registros de la cola.
   * @param items Registros de la cola.
   * @returns Mayor id, o 0 si no hay registros.
   */
  private maxQueueId(items: FrameQueueItem[]): number {
    return items.reduce((max, item) => Math.max(max, item.id || 0), 0);
  }

  /** Detiene la consulta periodica de la cola de frames. */
  private stopQueuePolling(): void {
    this.queuePolling = false;
    this.stopQueue$.next();
  }

  /**
   * Id desde el que interesan los registros de la cola: el mas antiguo de los
   * equipos que siguen esperando respuesta.
   * @returns Id de referencia para la consulta.
   */
  private queuePollSinceId(): number {
    const ids = this.feeders
      .filter((feeder) => this.isWaitingResponse(feeder))
      .map((feeder) => feeder.queue_since_id || 0);
    return ids.length > 0 ? Math.min(...ids) : this.queueLastId;
  }

  /**
   * Pide la cola de cada gateway del lote y une los resultados. Un gateway que
   * falle no corta la consulta de los demas.
   * @param sinceId Solo se necesitan los registros posteriores a este id.
   * @returns Registros de la cola de todos los gateways del lote.
   */
  private fetchQueue(sinceId: number) {
    const ids = this.queueGatewayIds();
    if (ids.length === 0) {
      return of([] as FrameQueueItem[]);
    }
    const requests = ids.map((id) =>
      this.fetchGatewayQueue(id, sinceId).pipe(catchError(() => of([] as FrameQueueItem[])))
    );
    return forkJoin(requests).pipe(
      map((groups: FrameQueueItem[][]) =>
        groups.reduce((items, group) => items.concat(group), [] as FrameQueueItem[])
      )
    );
  }

  /**
   * Lee la cola de un gateway desde la ultima pagina hacia atras.
   *
   * El backend la devuelve del registro mas viejo al mas nuevo y los enviados
   * duran cerca de una hora, asi que los recientes quedan al final. La primera
   * pagina da el total y el tamano real de pagina (el backend puede entregar
   * menos de los pedidos); luego se recorren las paginas finales hasta llegar a
   * registros anteriores a `sinceId`.
   * @param gatewayId Gateway a consultar.
   * @param sinceId Solo se necesitan los registros posteriores a este id.
   * @returns Registros de las paginas recorridas.
   */
  private fetchGatewayQueue(gatewayId: number, sinceId: number) {
    const query = (page: number, size: number) =>
      this.framesService.frameQueue(`?gateway_id=${gatewayId}&page_size=${size}&page=${page}`);

    return query(1, PANEL_QUEUE_PAGE_SIZE).pipe(
      switchMap((first: any) => {
        const count = first && first.count ? first.count : 0;
        const firstItems: FrameQueueItem[] = (first && first.results) || [];
        if (count <= firstItems.length) {
          return of(firstItems);
        }
        const lastPage = Math.ceil(count / firstItems.length);
        const read = (page: number) =>
          query(page, PANEL_QUEUE_PAGE_SIZE).pipe(
            map((resp: any) => ({ page, items: (resp && resp.results) || [] }))
          );
        return read(lastPage).pipe(
          /* Sigue hacia atras mientras la pagina empiece despues de `sinceId`. */
          expand((chunk: { page: number; items: FrameQueueItem[] }) =>
            chunk.page > 1 && chunk.items.length > 0 && chunk.items[0].id > sinceId
              ? read(chunk.page - 1)
              : EMPTY
          ),
          reduce(
            (items: FrameQueueItem[], chunk: { page: number; items: FrameQueueItem[] }) =>
              items.concat(chunk.items),
            [] as FrameQueueItem[]
          )
        );
      })
    );
  }

  /**
   * Gateways de los que dependen los equipos del lote.
   * @returns Identificadores de gateway sin repetir.
   */
  private queueGatewayIds(): number[] {
    const ids = this.feeders.map((feeder) => {
      const gateway = feeder.gateway;
      return gateway && typeof gateway === "object" ? gateway.id : gateway;
    });
    const fromFeeders = ids.filter((id, index) => !!id && ids.indexOf(id) === index);
    return fromFeeders.length > 0 ? fromFeeders : this.gateways.map((gateway) => gateway.id);
  }

  /**
   * Asigna a cada equipo en espera el estado de su frame en la cola.
   *
   * Solo se marcan los equipos que siguen esperando respuesta, y solo con los
   * registros posteriores a su envio: los enviados permanecen en la cola cerca
   * de una hora y se confundirian con envios anteriores.
   * @param items Registros de la cola.
   */
  private applyQueue(items: FrameQueueItem[]): void {
    const byMac: { [mac: string]: FrameQueueItem[] } = {};
    items.forEach((item) => {
      /* El backend puede dejar `address` vacio: la mac tambien viaja en la trama. */
      const mac = this.normalizeMac(item.address) || this.macFromPayload(item.payload);
      if (mac) {
        byMac[mac] = (byMac[mac] || []).concat(item);
      }
    });

    this.commit((feeders) =>
      feeders.map((feeder) => {
        const queue_status = this.isWaitingResponse(feeder)
          ? this.feederQueueStatus(byMac[this.normalizeMac(feeder.mac)], feeder.queue_since_id)
          : null;
        return feeder.queue_status === queue_status ? feeder : { ...feeder, queue_status };
      })
    );
    this.queueLastId = Math.max(this.queueLastId, this.maxQueueId(items));
  }

  /**
   * Quita el estado de cola a los equipos que ya no esperan respuesta. Se hace
   * en cada cambio del panel para que la pildora desaparezca en cuanto el equipo
   * responde, aunque ya no haya otra consulta de la cola.
   * @param feeders Lista de alimentadores.
   * @returns Lista sin estado de cola en los equipos que ya respondieron.
   */
  private clearAnsweredQueue(feeders: PanelFeeder[]): PanelFeeder[] {
    return feeders.map((feeder) =>
      feeder.queue_status && !this.isWaitingResponse(feeder)
        ? { ...feeder, queue_status: null }
        : feeder
    );
  }

  /**
   * Estado en la cola de un equipo a partir de sus registros posteriores al envio.
   * @param items Registros de la cola del equipo.
   * @param sinceId Ultimo id de la cola conocido al enviarle el comando.
   * @returns "waiting" si alguno sigue en espera, "sent" si ya salieron, o null.
   */
  private feederQueueStatus(items: FrameQueueItem[], sinceId: number): PanelQueueStatus | null {
    const own = (items || []).filter((item) => item.id > (sinceId || 0));
    if (own.length === 0) {
      return null;
    }
    /* Con varios registros del mismo equipo prevalece el que sigue en espera. */
    return own.some((item) => item.status !== PANEL_QUEUE_SENT_STATUS) ? "waiting" : "sent";
  }

  /**
   * Cuenta los equipos en espera segun el estado de su frame en la cola.
   * @returns Equipos con frame en espera y con frame ya enviado desde la cola.
   */
  private buildQueueSummary(): PanelQueueSummary {
    const statuses = this.feeders
      .filter((feeder) => this.isWaitingResponse(feeder))
      .map((feeder) => feeder.queue_status);
    return {
      waiting: statuses.filter((status) => status === "waiting").length,
      sent: statuses.filter((status) => status === "sent").length
    };
  }

  /**
   * Indica si el equipo tiene algun comando esperando respuesta.
   * @param feeder Alimentador a evaluar.
   * @returns `true` si algun comando sigue en espera.
   */
  private isWaitingResponse(feeder: PanelFeeder): boolean {
    return Object.keys(feeder.loadings).some((key) => feeder.loadings[key] === "loading");
  }

  /**
   * Extrae la mac destino de una trama XBee de transmision en base64.
   *
   * La trama empieza con el tipo (1 byte) y el id de frame (1 byte), seguidos
   * de la direccion de 64 bits del destino (8 bytes), que es la mac del equipo.
   * @param payload Trama en base64.
   * @returns Mac normalizada, o cadena vacia si la trama no es de transmision.
   */
  private macFromPayload(payload: string): string {
    if (!payload) {
      return "";
    }
    let bytes = "";
    try {
      bytes = atob(payload);
    } catch (error) {
      return "";
    }
    if (bytes.length < 10 || XBEE_TRANSMIT_FRAME_TYPES.indexOf(bytes.charCodeAt(0)) < 0) {
      return "";
    }
    return Array.from(bytes.slice(2, 10))
      .map((char) => `0${char.charCodeAt(0).toString(16)}`.slice(-2))
      .join("");
  }

  /**
   * Normaliza una mac para compararla sin importar mayusculas ni separadores.
   * @param mac Mac a normalizar.
   * @returns Mac en minusculas y sin separadores.
   */
  private normalizeMac(mac: string): string {
    return String(mac ? mac : "")
      .replace(/[^0-9a-z]/gi, "")
      .toLowerCase();
  }

  /* ------------------------------------------------------------------ */
  /* Envio de comandos                                                   */
  /* ------------------------------------------------------------------ */

  /**
   * Decide a que equipos se envia un comando.
   *
   * Si hay equipos marcados manda solo a esos; si no, a todos los visibles.
   * Encender y apagar se acotan ademas al subconjunto que lo necesita.
   * @param key Comando a enviar.
   * @returns Alimentadores destinatarios.
   */
  private resolveTargets(key: PanelCommandKey): PanelFeeder[] {
    const checked = this.feeders.filter((feeder) => feeder.checked);
    const base = checked.length > 0 ? checked : this.feeders;

    if (PANEL_POWER_COMMANDS.indexOf(key) < 0) {
      return base;
    }
    return base.filter((feeder) =>
      key === "turn_on" ? !feeder.status_power : feeder.status_power
    );
  }

  /**
   * Reune los estanques de un grupo de alimentadores, sin repetir.
   * @param feeders Alimentadores destinatarios.
   * @returns Identificadores de estanque.
   */
  private resolvePonds(feeders: PanelFeeder[]): number[] {
    const ponds = feeders.map((feeder) => {
      const pond = feeder.pond;
      return pond && typeof pond === "object" ? pond.id : pond;
    });
    return ponds.filter((pond, index) => pond && ponds.indexOf(pond) === index);
  }

  /**
   * Marca un comando como en espera para los equipos indicados y abre su
   * entrada en el historial.
   * @param ids Identificadores de los alimentadores.
   * @param key Comando enviado.
   */
  private markLoading(ids: number[], key: PanelCommandKey): void {
    const time = moment().format("HH:mm:ss");
    this.commit((feeders) =>
      feeders.map((feeder) => {
        if (ids.indexOf(feeder.id) < 0) {
          return feeder;
        }
        return {
          ...feeder,
          condition: true,
          text_condition: "",
          loadings: { ...feeder.loadings, [key]: "loading" },
          alerts: this.upsertAlert(feeder.alerts, key, time),
          queue_status: null,
          queue_since_id: this.queueLastId
        };
      })
    );
  }

  /**
   * Abre o reinicia la entrada de historial de un comando.
   * @param alerts Historial actual del equipo.
   * @param key Comando enviado.
   * @param time Hora de envio.
   * @returns Historial nuevo con la entrada del comando en espera.
   */
  private upsertAlert(
    alerts: PanelAlertEntry[],
    key: PanelCommandKey,
    time: string
  ): PanelAlertEntry[] {
    const exists = alerts.some((alert) => alert.alert === key);
    const opened: PanelAlertEntry = {
      alert: key,
      data: null,
      timeInitial: time,
      error: false,
      alert_comm: false,
      server_error: false
    };
    if (!exists) {
      return [...alerts, opened];
    }
    return alerts.map((alert) => (alert.alert === key ? opened : alert));
  }

  /**
   * Traduce la respuesta del backend en errores por equipo.
   *
   * El gateway responde con un diccionario indexado por MAC; un equipo que no
   * aparece en el se considera aceptado.
   * @param key Comando enviado.
   * @param response Respuesta o error de la peticion.
   */
  private applyCommandErrors(key: PanelCommandKey, response: any): void {
    if (!response) {
      return;
    }

    const errors = response.errors || (response.error && response.error.errors) || null;
    const detail =
      response.error && Array.isArray(response.error.detail) && response.error.detail.length > 0
        ? response.error.detail[0]
        : "";

    if (!errors && !detail) {
      return;
    }

    /* Un error general (sin detalle por MAC) suele venir de un gateway
       desconectado: si se sabe que equipos cuelgan de un gateway caido, solo
       esos fallan; el resto sigue esperando su respuesta por socket. */
    const loading = this.feeders.filter((feeder) => feeder.loadings[key] === "loading");
    const downIds = loading.filter((feeder) => this.isGatewayDown(feeder)).map((f) => f.id);
    const generalTargets = downIds.length > 0 ? downIds : loading.map((feeder) => feeder.id);

    this.commit((feeders) =>
      feeders.map((feeder) => {
        if (feeder.loadings[key] !== "loading") {
          return feeder;
        }
        if (detail && !errors) {
          /* Sin texto en la tarjeta: el estado del gateway ya se ve en el pie. */
          return generalTargets.indexOf(feeder.id) < 0 ? feeder : this.failCommand(feeder, key, "");
        }
        if (!errors || !Object.prototype.hasOwnProperty.call(errors, feeder.mac)) {
          return feeder;
        }

        const message = String(errors[feeder.mac]);
        /* El gateway aun esta procesando un envio anterior: sigue en espera. */
        if (message === "Already has a frame in progress") {
          return {
            ...feeder,
            condition: false,
            text_condition: this.translatorService.translate("panel-alerts.frame-in-progress")
          };
        }
        /* El gateway desconectado ya se ve en el pie: no se repite en la tarjeta. */
        return this.failCommand(feeder, key, GATEWAY_DOWN_PATTERN.test(message) ? "" : message);
      })
    );
  }

  /**
   * Marca un comando como rechazado por el servidor para un equipo: su fila
   * pasa a error y solo la nube de ese comando se pone en rojo.
   * @param feeder Alimentador afectado.
   * @param key Comando rechazado.
   * @param message Motivo que devolvio el backend; vacio para no mostrar texto.
   * @returns Alimentador con el comando en error.
   */
  private failCommand(feeder: PanelFeeder, key: PanelCommandKey, message: string): PanelFeeder {
    return {
      ...feeder,
      condition: !message,
      text_condition: message,
      loadings: { ...feeder.loadings, [key]: "error" },
      alerts: feeder.alerts.map((alert) =>
        alert.alert === key ? { ...alert, server_error: true } : alert
      )
    };
  }

  /**
   * Indica si el gateway del que cuelga un equipo esta desconectado, segun el
   * ultimo estado conocido (carga inicial + eventos del socket).
   * @param feeder Alimentador a evaluar.
   * @returns `true` si su gateway se sabe desconectado.
   */
  private isGatewayDown(feeder: PanelFeeder): boolean {
    const gateway = feeder.gateway;
    if (gateway === null || gateway === undefined) {
      return false;
    }
    const id = typeof gateway === "object" ? gateway.id : gateway;
    const known = this.gateways.find((item) => item.id === id);
    if (known) {
      return !known.connected;
    }
    return typeof gateway === "object" && gateway.connected === false;
  }

  /**
   * Resuelve el sufijo de endpoint de un comando.
   * @param key Comando a enviar.
   * @returns Sufijo que espera FramesService.
   */
  private endpointFor(key: PanelCommandKey): string {
    const definition = PANEL_COMMANDS.find((item) => item.key === key);
    return definition && definition.endpoint ? definition.endpoint : key;
  }

  /**
   * Construye el texto de error del vaciado.
   *
   * El equipo tiene que estar encendido para poder vaciarse, asi que ese caso
   * se explica aparte en lugar de mostrar el mensaje crudo del backend.
   * @param message Mensaje devuelto por el backend.
   * @param affected Alimentadores incluidos en la peticion.
   * @returns Texto a mostrar en la tarjeta del equipo.
   */
  private emptyErrorText(message: any, affected: any[]): string {
    const anyOff = (affected || []).some((feeder) => !feeder.status_power);
    if (anyOff) {
      return this.translatorService.translate("panel-alerts.must-turn-on");
    }
    return message
      ? String(message)
      : this.translatorService.translate("panel-alerts.feeder-error");
  }

  /* ------------------------------------------------------------------ */
  /* Contexto                                                            */
  /* ------------------------------------------------------------------ */

  /**
   * Filtra los comandos segun el rol y los permisos del usuario.
   * @returns Comandos que el usuario actual puede lanzar.
   */
  private resolveVisibleCommands(): PanelCommandDefinition[] {
    const isAdmin = this.storageService.validateSoloAdmin();
    const alertsOnly = this.storageService.hasAlertsOnlyPermission();

    return PANEL_COMMANDS.filter((command) => {
      if (command.admin_only && !isAdmin) {
        return false;
      }
      return !(command.hidden_for_alerts_only && alertsOnly);
    });
  }

  /** Carga la zona horaria de la granja para fechar las tramas. */
  private loadTimezone(): void {
    const farmId = this.resolveFarmId();
    if (!farmId) {
      return;
    }
    this.farmService
      .getFarm(farmId, `?fields=timezone`)
      .pipe(takeUntil(this.destroy$))
      .subscribe((resp: any) => {
        this.timezone = resp ? resp.timezone : "";
      });
  }

  /** Carga los gateways del lote para el indicador del pie y el manejo de errores. */
  private loadGateways(): void {
    /* Si los equipos ya traen su gateway se usa ese directamente */
    const fromFeeders = this.feederGateways();
    if (fromFeeders.length > 0) {
      this.gateways = fromFeeders;
      this.loadQueueBaseline();
      return;
    }
    const ids = this.feeders.map((feeder) => feeder.id).filter((id) => id !== undefined);
    if (ids.length === 0) {
      return;
    }
    /* Las listas no traen el gateway: se pide solo para los equipos del lote, que
       pueden ser de distintas granjas o páginas */
    const query = this.globalFunction.constructQuery({
      id_in: ids.join(","),
      page_size: ids.length,
      fields: ["id", { gateway: ["id", "name", "connected"] }]
    });
    this.feedersService
      .ListFeedersQuery(query)
      .pipe(takeUntil(this.destroy$))
      .subscribe((resp: any) => {
        const rows = Array.isArray(resp) ? resp : resp && resp.results ? resp.results : [];
        const gatewayById: { [id: number]: any } = {};
        rows.forEach((row) => (gatewayById[row.id] = row.gateway));
        this.commit((feeders) =>
          feeders.map((feeder) =>
            gatewayById[feeder.id] ? { ...feeder, gateway: gatewayById[feeder.id] } : feeder
          )
        );
        this.gateways = this.feederGateways();
        this.loadQueueBaseline();
      });
  }

  /**
   * Gateways que traen los equipos del lote, sin repetir y con cuantos equipos
   * dependen de cada uno.
   * @returns Gateways del lote; vacio si los equipos no traen ese dato.
   */
  private feederGateways(): PanelGatewayInfo[] {
    const gateways: PanelGatewayInfo[] = [];
    this.feeders.forEach((feeder) => {
      const gateway = feeder.gateway;
      if (!gateway || typeof gateway !== "object") {
        return;
      }
      const known = gateways.find((item) => item.id === gateway.id);
      if (known) {
        known.feeders = known.feeders + 1;
        return;
      }
      gateways.push({
        id: gateway.id,
        name: gateway.name,
        connected: !!gateway.connected,
        feeders: 1
      });
    });
    return gateways;
  }

  /**
   * Resuelve la granja del lote actual.
   *
   * Manda la granja de los propios equipos: el almacenamiento local guarda la
   * ultima granja visitada, que no tiene por que ser la de estos equipos (por
   * ejemplo en el apartado de alertas, donde se mezclan clientes). Solo si los
   * equipos no la traen se usa la del almacenamiento local.
   * @returns Identificador de granja, o cadena vacia si no se puede resolver.
   */
  private resolveFarmId(): number | string {
    const first: any = this.feeders[0];
    if (first && first.farm) {
      return typeof first.farm === "object" ? first.farm.id : first.farm;
    }
    if (first && first.pond && typeof first.pond === "object" && first.pond.farm) {
      return first.pond.farm;
    }
    return this.storageService.localValue.farm_id || "";
  }

  /** Mantiene al dia el texto de "ultima respuesta" sin tocar el estado. */
  private watchLastResponse(): void {
    timer(LAST_RESPONSE_TICK_MS, LAST_RESPONSE_TICK_MS)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.lastResponseLabel = this.buildLastResponseLabel();
      });
  }

  /**
   * Construye el texto relativo de la ultima respuesta recibida.
   *
   * El idioma de moment se fija en cada llamada porque otras pantallas lo
   * cambian de forma global y el panel debe seguir al idioma de la interfaz.
   * @returns Texto ya traducido, o cadena vacia si aun no hay respuestas.
   */
  private buildLastResponseLabel(): string {
    if (!this.lastResponseAtIso) {
      return "";
    }
    const language = this.translatorService.translateService.currentLang;
    return moment(this.lastResponseAtIso)
      .locale(language === "en" ? "en" : "es")
      .fromNow();
  }

  /**
   * Calcula el tiempo transcurrido desde que se envio un comando.
   * @param initial Hora de envio en formato HH:mm:ss.
   * @returns Duracion en formato H:m:s.
   */
  private elapsedSince(initial: string): string {
    const start = moment(initial || "", "HH:mm:ss");
    const end = moment(moment().format("HH:mm:ss"), "HH:mm:ss");
    const duration = moment.duration(end.diff(start));
    return `${duration.hours()}:${duration.minutes()}:${duration.seconds()}`;
  }

  /** Cierra el modal y avisa al contenedor. */
  private dismiss(): void {
    this.modalService.dismissAll();
    this.modalRef = null;
    this.closed.emit(false);
  }
}
